import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { PermissionsContext } from "../../utils/PermissionsContext";
import { useUser } from "../UserContext";
import api from "../../utils/api";

// Subcomponents
import AddWorkOrder from "./addWorkorder";
import AddArchitecturalRequest from "./AddArchitecturalRequest";
import ArchitecturalRequestByMember from "./ArchitecturalRequestbyMember";
import MemberDetailsCard from "./MemberDetailsCard";
import ActivityTimeline from "./ActivityTimeline";
import ViolationsList from "./ViolationsList";
import AddViolationForm from "./AddViolationForm";
import WorkOrdersList from "./WorkOrdersList";
import GateVisitorsList from "./GateVisitorsList";
import AddGateVisitorForm from "./AddGateVisitorForm";
import MemberAnalyticsPanel from "./MemberAnalyticsPanel";

const MemberProfilePage = () => {
  const { ClubMemberCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromPage = queryParams.get("from");
  const { user } = useUser();
  const permissionsCtx = useContext(PermissionsContext);

  const hasPermission =
    typeof permissionsCtx === "function"
      ? permissionsCtx
      : (perm) => Array.isArray(permissionsCtx) && permissionsCtx.includes(perm);

  const [member, setMember] = useState(null);
  const [violations, setViolations] = useState([]);
  const [violationTypes, setViolationTypes] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    property_id: ClubMemberCode || "",
    account_id: "",
    ccr_violation_type_id: "",
    inspection_id: "1",
    cure_by:
      `${localStorage.getItem("firstName") || ""} ${
        localStorage.getItem("lastName") || ""
      }`.trim() || "Unknown User",
    comment: "",
    upload_urls: null,
    violated_at: "",
  });

  // 👤 Derive openerUserId just like OwnerDashboard
  const openerUserId = useMemo(() => {
    const ctxId = user?.id ?? user?.user_id ?? user?.UserId ?? user?.ID ?? null;
    if (ctxId != null && Number.isFinite(Number(ctxId))) return Number(ctxId);

    const direct = localStorage.getItem("userId") || localStorage.getItem("uid");
    if (direct && /^\d+$/.test(String(direct))) return Number(direct);

    const objStr =
      localStorage.getItem("user") ||
      localStorage.getItem("currentUser") ||
      localStorage.getItem("authUser");
    if (objStr) {
      try {
        const obj = JSON.parse(objStr);
        const id = obj?.id ?? obj?.userId ?? obj?.uid ?? null;
        if (id != null && Number.isFinite(Number(id))) return Number(id);
      } catch {
        /* ignore */
      }
    }
    return null;
  }, [user]);

  useEffect(() => {
   
  }, [openerUserId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [memberRes, violationTypesRes, violationsRes, statusesRes, workOrdersRes] =
          await Promise.all([
            api.get(`/members/${ClubMemberCode}`),
            api.get(`/getCcrViolationTypes`),
            api.get(`/getViolations`, { params: { account_id: ClubMemberCode } }),
            api.get(`/statuses`),
            api.get(`/getWorkOrders`, { params: { property_id: ClubMemberCode } }),
          ]);

        setMember(memberRes.data);
        setViolationTypes(violationTypesRes.data);
        setViolations(violationsRes.data);
        setStatuses(statusesRes.data);
        setWorkOrders(workOrdersRes.data);

        const now = new Date();
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(now.getDate() - 14);

        const recentViolations = violationsRes.data
          .filter(
            (v) =>
              new Date(v.updated_at) >= twoWeeksAgo &&
              new Date(v.updated_at) <= now
          )
          .map((v) => ({
            date: v.updated_at,
            message: `Violation ${v.status.toUpperCase()}`,
          }));

        const recentWorkOrders = workOrdersRes.data
          .filter(
            (o) =>
              new Date(o.updated_at) >= twoWeeksAgo &&
              new Date(o.updated_at) <= now
          )
          .map((o) => ({
            date: o.updated_at,
            message: `Work Order ${
              o.resolved_at ? "Resolved" : "Unresolved"
            }: ${o.description || "No description available"}`,
          }));

        setActivity(
          [...recentViolations, ...recentWorkOrders].sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );

        setFormData((prev) => ({
          ...prev,
          property_id: memberRes.data.ClubMemberCode,
          account_id: memberRes.data.ClubMemberCode,
        }));
      } catch (err) {
        console.error(err);
        setError("Failed to load member profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ClubMemberCode]);

  const handleTabChange = (e, newIndex) => setTabIndex(newIndex);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, upload_urls: file }));
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.ccr_violation_type_id || !formData.violated_at) {
      alert("Please fill in all required fields.");
      return;
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) =>
      formDataToSend.append(key, value)
    );

    try {
      await api.post(`/addViolation`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Violation added successfully.");
      setFormData((prev) => ({
        ...prev,
        ccr_violation_type_id: "",
        comment: "",
        violated_at: "",
        upload_urls: null,
      }));
      setPreviewImage(null);
    } catch (err) {
      console.error("Failed to add violation:", err);
      alert("Failed to add violation.");
    }
  };

  const tabConfig = [
    {
      label: "Violations",
      component: (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Violations History
          </Typography>
          <Divider sx={{ my: 2 }} />
          <ViolationsList
            violations={violations}
            violationTypes={violationTypes}
            navigate={navigate}
          />
        </>
      ),
      permission: "view_violations",
    },
    {
      label: "Add Violation",
      component: (
        <AddViolationForm
          formData={formData}
          setFormData={setFormData}
          handleInputChange={handleInputChange}
          handleFileChange={handleFileChange}
          handleSubmit={handleSubmit}
          previewImage={previewImage}
          violationTypes={violationTypes}
        />
      ),
      permission: "add_violation",
    },
    {
      label: "Work Orders",
      component: (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Work Orders
          </Typography>
          <Divider sx={{ my: 2 }} />
          <WorkOrdersList
            workOrders={workOrders}
            statuses={statuses}
            navigate={navigate}
          />
        </>
      ),
      permission: "view_work_orders",
    },
    {
      label: "Add Work Orders",
      component: (
        <AddWorkOrder
          propertyId={member?.ClubMemberCode}
          openedByUserId={openerUserId}   
          uploadedBy={openerUserId ?? 1}  
        />
      ),
      permission: "view_work_orders",
    },
    {
      label: "Architectural Request",
      component: (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Architectural Request
          </Typography>
          <Divider sx={{ my: 2 }} />
          <ArchitecturalRequestByMember
            propertyId={member?.ClubMemberCode?.trim()}
            accountId={member?.ClubMemberCode?.trim()}
          />
        </>
      ),
      permission: "view_architectural_requests",
    },
    {
      label: "Add Architectural Request",
      component: (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Add Architectural Request
          </Typography>
          <Divider sx={{ my: 2 }} />
          <AddArchitecturalRequest
            propertyId={member?.ClubMemberCode}
            accountId={member?.ClubMemberCode}
          />
        </>
      ),
      permission: "add_architectural_request",
    },
    {
      label: "Gate Visitors",
      component: <GateVisitorsList clubMemberCode={member?.ClubMemberCode} />,
      permission: "view_gate_visitors",
    },
    {
      label: "Add Gate Visitor",
      component: <AddGateVisitorForm clubMemberCode={member?.ClubMemberCode} />,
      permission: "add_gate_visitor",
    },
  ];

  const visibleTabs = tabConfig.filter(
    ({ permission }) => !permission || hasPermission(permission)
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          fromPage === "properties"
            ? navigate("/home/properties")
            : navigate("/home/clubmembers")
        }
        sx={{ mb: 3 }}
      >
        {fromPage === "properties" ? "Back" : "Back"} to Members
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <MemberDetailsCard member={member} />
          <ActivityTimeline activity={activity} />
          <MemberAnalyticsPanel violations={violations} workOrders={workOrders} />
        </Grid>

        <Grid item xs={12} md={8}>
          {visibleTabs.length > 0 ? (
            <>
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                {visibleTabs.map((tab, index) => (
                  <Tab key={index} label={tab.label} />
                ))}
              </Tabs>

              <Box sx={{ mt: 2 }}>{visibleTabs[tabIndex]?.component}</Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary">
              You do not have access to any member-related actions.
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MemberProfilePage;
